import { Button, Divider, SkeletonBox, Space, Text } from '@billy/design-system'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'

import { BankTransactionSortProperty } from '@features/bankTransactions/enums/bankTransactionSortProperty'
import { TransactionTypeFilter } from '@features/bankTransactions/enums/transactionTypeFilter'

import { SortDirection } from '../../../../../../../../../enums/sortDirection'
import { useBankTransactionDrawer } from '../../../../../contexts'
import { useSandboxBankTransactions } from '../../../../../hooks/useSandboxBankTransactions'
import { TransactionItem } from '../../TransactionItem'
import { TransactionsListSearch } from './elements/TransactionsListSearch'
import { TransactionsListSort } from './elements/TransactionsListSort'

export interface LinkableTransactionsFilters {
  searchQuery?: string
  sortDirection: SortDirection
  sortProperty: BankTransactionSortProperty
  transactionSideFilter?: TransactionTypeFilter
  minAmount?: number
  maxAmount?: number
}

const defaultFilters: LinkableTransactionsFilters = {
  searchQuery: undefined,
  sortDirection: SortDirection.Desc,
  sortProperty: BankTransactionSortProperty.EntryDate,
  transactionSideFilter: undefined,
  minAmount: undefined,
  maxAmount: undefined,
}

interface TransactionsListProps {
  showAll?: boolean
  onViewAll?: () => void
}

export const TransactionsList = ({ showAll = false, onViewAll }: TransactionsListProps): ReactElement => {
  const { t } = useTranslation()
  const { selectedTransaction, combinedTransactions, toggleCombinedTransactionsSelection, currency, accountId } =
    useBankTransactionDrawer()
  const [linkableTransactionsFilters, setLinkableTransactionsFilters] =
    useState<LinkableTransactionsFilters>(defaultFilters)

  const { bankTransactions, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useSandboxBankTransactions({
    accountId: accountId || '',
    searchQuery: linkableTransactionsFilters.searchQuery,
    sortDirection: linkableTransactionsFilters.sortDirection,
    sortProperty: linkableTransactionsFilters.sortProperty,
    transactionTypeFilter: linkableTransactionsFilters.transactionSideFilter,
    minAmount: linkableTransactionsFilters.minAmount,
    maxAmount: linkableTransactionsFilters.maxAmount,
    enabled: !!selectedTransaction,
    isReconciled: false,
  })

  const { ref: lastItemRef, inView } = useInView()

  const filteredBankTransactions = useMemo(
    () => bankTransactions.filter((transaction) => transaction.id !== selectedTransaction?.id),
    [bankTransactions, selectedTransaction],
  )

  const handleSortChange = (sortProperty: BankTransactionSortProperty, sortDirection: SortDirection) => {
    setLinkableTransactionsFilters((prevFilters) => ({
      ...prevFilters,
      sortProperty,
      sortDirection,
    }))
  }

  const handleSearchChange = (searchQuery?: string) => {
    setLinkableTransactionsFilters((prevFilters) => ({
      ...prevFilters,
      searchQuery,
    }))
  }

  const handleAmountRangeChange = (minAmount?: number, maxAmount?: number) => {
    setLinkableTransactionsFilters((prevFilters) => ({
      ...prevFilters,
      minAmount,
      maxAmount,
    }))
  }

  const handleTransactionSideChange = (transactionSideFilter?: TransactionTypeFilter) => {
    setLinkableTransactionsFilters((prevFilters) => ({
      ...prevFilters,
      transactionSideFilter,
    }))
  }

  const handleClearAllFilters = () => {
    setLinkableTransactionsFilters(defaultFilters)
  }

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  if (showAll) {
    return (
      <div>
        <TransactionsListSearch
          filters={linkableTransactionsFilters}
          currency={currency}
          onSearchChange={handleSearchChange}
          onAmountRangeChange={handleAmountRangeChange}
          onTransactionSideChange={handleTransactionSideChange}
          resetFilters={handleClearAllFilters}
        />
        <Space size="s16" />
        <TransactionsListSort
          sortProperty={linkableTransactionsFilters.sortProperty}
          sortDirection={linkableTransactionsFilters.sortDirection}
          onSortChange={handleSortChange}
        />
        <Space size="s8" />
        {isLoading && <SkeletonBox fullWidth height={30} />}
        {!isLoading && filteredBankTransactions.length === 0 ? (
          <Text colorVariant="discreet">{t('bank_transactions.drawer.no_items')}</Text>
        ) : (
          filteredBankTransactions.map((item, index) => (
            <div key={item.id}>
              <TransactionItem
                key={item.id}
                transactionItem={item}
                currency={currency}
                showSignedAmount
                showCheckbox
                checked={combinedTransactions.has(item.id)}
                onCheckedChange={() => toggleCombinedTransactionsSelection(item)}
              />
              {index < filteredBankTransactions.length - 1 && <Divider />}
            </div>
          ))
        )}
        {isFetchingNextPage && <SkeletonBox fullWidth height={30} />}
        {hasNextPage && <div ref={lastItemRef} />}
      </div>
    )
  }

  return isLoading ? (
    <SkeletonBox fullWidth height={30} />
  ) : (
    <div>
      <Text weight="medium">{t('bank_transactions.drawer.transactions.subtitle')}</Text>
      <Space size="s8" />
      {Array.from(combinedTransactions.values()).map((transaction) => (
        <TransactionItem
          key={transaction.id}
          transactionItem={transaction}
          currency={currency}
          showSignedAmount
          showCheckbox
          checked
          onCheckedChange={() => toggleCombinedTransactionsSelection(transaction)}
        />
      ))}
      <Space size="s8" />
      <Button fullWidth onClick={onViewAll} variant="outlined">
        {t('bank_transactions.drawer.transactions.see_all')}
      </Button>
    </div>
  )
}
